/**
 * WHY EACH COMP WAS SET ASIDE — the words the user reads for the reason the engine wrote.
 *
 * The engine now records, per comp, the reason at the stage that dropped it (see
 * `_trail_note` in backend/analysis_engine.py). Before that, the reason was guessed after
 * the fact and everything the guess could not place was shown as "Distressed / atípico" —
 * on the 22-property corpus that label sat on 3,440 of 3,440 rows, none of which had been
 * dropped for being distressed. The user's own house was among them.
 *
 * This module is the only place that turns a reason code into text. It is pure so it can
 * be tested as a table: code + detail in, sentence out. Unknown codes get a truthful
 * generic label, never a specific one they did not earn.
 */

export const EXCLUDED_ROWS_SHOWN = 15;

// null/undefined/'' are "unknown", not zero: Number(null) is 0, and a comp with no
// distance must not sort as the closest one.
const n = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const money = (v) => (n(v) == null ? '' : `$${Math.round(n(v)).toLocaleString()}`);
const ordinal = (k, lang) => {
  const i = n(k);
  if (i == null) return '';
  if (lang !== 'en') return `${i}º`;
  const s = ['th', 'st', 'nd', 'rd'];
  const v = i % 100;
  return `${i}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/**
 * The tier qualifier's strings are machine-shaped: "distance_6.2mi_exceeds_5mi",
 * "sqft_diff_61pct_exceeds_40pct", "age_400d_exceeds_365d", "property_type_mismatch",
 * "severely_distressed", "invalid_data", "oversized_multifamily_4units_vs_2". Read the
 * numbers out where there are numbers; name the rule where there are not.
 */
export function describeNotQualified(detail, lang) {
  const d = String(detail || '');
  const en = lang === 'en';
  let m;
  if ((m = d.match(/^distance_([\d.]+)mi_exceeds_([\d.]+)mi$/))) {
    return en ? `${m[1]} mi away — beyond the ${m[2]} mi limit` : `A ${m[1]} mi — más allá del límite de ${m[2]} mi`;
  }
  if ((m = d.match(/^sqft_diff_(\d+)pct_exceeds_(\d+)pct$/))) {
    return en ? `${m[1]}% size difference — beyond the ${m[2]}% limit` : `${m[1]}% de diferencia de tamaño — más allá del límite de ${m[2]}%`;
  }
  if ((m = d.match(/^age_(\d+)d_exceeds_(\d+)d$/))) {
    return en ? `Sold ${m[1]} days ago — older than the ${m[2]}-day limit` : `Vendido hace ${m[1]} días — más viejo que el límite de ${m[2]} días`;
  }
  if (d === 'property_type_mismatch') return en ? 'Different property type' : 'Otro tipo de propiedad';
  if (d.startsWith('oversized_multifamily')) return en ? 'Too many units to compare' : 'Demasiadas unidades para comparar';
  if (d === 'severely_distressed') return en ? 'Priced as a ruin' : 'Precio de ruina';
  if (d === 'invalid_data') return en ? 'Missing price or size' : 'Falta precio o tamaño';
  return en ? 'Did not meet the comparison filters' : 'No pasó los filtros de comparación';
}

/**
 * Returns { label, note } — `label` is the short chip, `note` the one-line explanation
 * carrying the numbers the engine recorded. Both localized.
 */
export function exclusionText(comp, lang = 'en') {
  const en = lang === 'en';
  const code = comp?.exclusion_reason || '';
  const d = comp?.exclusion_detail || {};
  switch (code) {
    case 'subject_property':
      return { label: en ? 'This is the property' : 'Es la propiedad', note: '' };
    case 'beyond_cap':
      return {
        label: en ? `Ranked ${ordinal(d.rank, lang)}` : `Quedó ${ordinal(d.rank, lang)}`,
        note: en
          ? `${ordinal(d.rank, lang)} of ${d.of} by closeness and recency — the engine uses the top ${d.cap}`
          : `${ordinal(d.rank, lang)} de ${d.of} por cercanía y fecha — el motor usa los ${d.cap} mejores`,
      };
    case 'looser_tier_not_needed':
      return {
        label: en ? 'Looser match' : 'Coincidencia más laxa',
        note: en
          ? 'Passed only a looser filter; the strict one already had enough comps'
          : 'Solo pasó un filtro más laxo; el estricto ya tenía suficientes comps',
      };
    case 'tighter_tier_dropped':
      return {
        label: en ? 'Strict match, not used' : 'Coincidencia estricta, no usada',
        note: en
          ? 'Passed the strict filter but the engine settled on a looser tier'
          : 'Pasó el filtro estricto pero el motor se quedó con un nivel más laxo',
      };
    case 'below_distressed_floor':
      return {
        label: en ? 'Priced as a ruin' : 'Precio de ruina',
        note: en
          ? `${money(d.ppsf)}/sqft — under the ${money(d.floor)}/sqft floor (half the pool median of ${money(d.median)})`
          : `${money(d.ppsf)}/sqft — bajo el piso de ${money(d.floor)}/sqft (la mitad de la mediana del pool, ${money(d.median)})`,
      };
    case 'different_price_tier':
      return {
        label: en ? 'Different price tier' : 'Otra gama de precio',
        note: en ? 'Priced above the tier the subject sits in' : 'Precio por encima de la gama del sujeto',
      };
    case 'different_market':
      return {
        label: en ? 'Different market' : 'Otro mercado',
        note: en ? 'A neighbouring city with a different price level' : 'Una ciudad vecina con otro nivel de precios',
      };
    case 'listing':
      return {
        label: en ? 'Not sold' : 'No vendido',
        note: en ? 'An active listing — an asking price, not a sale' : 'Listado activo — precio pedido, no venta',
      };
    case 'foreclosure_short_sale':
      return {
        label: en ? 'Foreclosure / short sale' : 'Ejecución / venta corta',
        note: en ? 'A forced sale is not evidence of market value' : 'Una venta forzada no es evidencia de valor de mercado',
      };
    case 'wrong_state':
      return { label: en ? 'Different state' : 'Otro estado', note: '' };
    case 'too_far':
      return { label: en ? 'Too far' : 'Muy lejos', note: en ? 'More than 5 mi away' : 'A más de 5 mi' };
    case 'not_qualified':
      return { label: en ? 'Not comparable' : 'No comparable', note: describeNotQualified(d.detail, lang) };
    case 'other_condition_cluster': {
      const lower = d.cluster === 'distressed';
      const upper = d.cluster === 'renovated';
      return {
        label: lower
          ? (en ? 'Lower price band' : 'Banda de precio inferior')
          : upper
            ? (en ? 'Upper price band' : 'Banda de precio superior')
            : (en ? 'Other price band' : 'Otra banda de precio'),
        note: en
          ? 'Priced in a different band than the comps the value is built on'
          : 'Precio en otra banda que los comps sobre los que se armó el valor',
      };
    }
    case 'below_price_gap':
      return {
        label: en ? 'Below the price gap' : 'Bajo el salto de precio',
        note: en
          ? `${money(d.ppsf)}/sqft — the pool splits at ${money(d.split_at)}/sqft (${d.separation}× apart)`
          : `${money(d.ppsf)}/sqft — el pool se parte en ${money(d.split_at)}/sqft (${d.separation}× de separación)`,
      };
    case 'below_p25':
      return {
        label: en ? 'Bottom quarter' : 'Cuarto inferior',
        note: en
          ? `${money(d.ppsf)}/sqft — under the pool's 25th percentile (${money(d.p25)})`
          : `${money(d.ppsf)}/sqft — bajo el percentil 25 del pool (${money(d.p25)})`,
      };
    case 'ppsf_outlier':
      return {
        label: en ? 'Price outlier' : 'Precio atípico',
        note: en
          ? `${money(d.ppsf)}/sqft — more than ${d.factor}× the median of the others (${money(d.cap)})`
          : `${money(d.ppsf)}/sqft — más de ${d.factor}× la mediana de los demás (${money(d.cap)})`,
      };
    case 'beyond_top5_distance':
      return {
        label: en ? `${ordinal(d.rank, lang)} closest` : `${ordinal(d.rank, lang)} más cercano`,
        note: en ? 'The engine keeps the 5 closest of the chosen band' : 'El motor se queda con los 5 más cercanos de la banda elegida',
      };
    case 'user_comps_only':
      return { label: en ? 'Your comps only' : 'Solo tus comps', note: en ? 'You chose to value on your own comps' : 'Elegiste valuar solo con tus comps' };
    case 'not_a_sale':
      return { label: en ? 'Not a sale' : 'No es venta', note: '' };
    case 'duplicate_address':
      return { label: en ? 'Duplicate' : 'Duplicado', note: '' };
    case 'v5_filter':
      return { label: en ? 'Filtered' : 'Filtrado', note: en ? 'Removed by the comp quality filter' : 'Quitado por el filtro de calidad de comps' };
    case 'not_selected':
    case 'distressed_or_filtered': // analyses stored before the engine wrote real reasons
      return { label: en ? 'Not selected' : 'No entró en la selección', note: '' };
    default:
      return { label: en ? 'Not selected' : 'No entró en la selección', note: '' };
  }
}

/**
 * The rows to render: the subject itself is not a comp and is dropped; the rest are
 * sorted by distance, closest first, so the top of the list holds the comps a user
 * would most expect to see used — which is exactly where the explanation has to be.
 * Unknown distances go last.
 */
export function orderExcluded(comps) {
  return (comps || [])
    .filter((c) => c && c.exclusion_reason !== 'subject_property')
    .slice()
    .sort((a, b) => {
      const da = n(a.distance);
      const db = n(b.distance);
      if (da == null && db == null) return 0;
      if (da == null) return 1;
      if (db == null) return -1;
      return da - db;
    });
}

/** First `limit` rows plus how many are held back. */
export function splitExcluded(ordered, limit = EXCLUDED_ROWS_SHOWN) {
  const shown = ordered.slice(0, limit);
  return { shown, hidden: Math.max(ordered.length - shown.length, 0) };
}
